import { CartItem } from '../../checkout-shared/reducers/checkout.reducer';
import { Pipe, PipeTransform } from '@angular/core';
import {
	getProductAssetUrl,
	hasApprovalWorkflow,
	isDownloadableProduct,
} from '@app/product/utils/product-utils';
import { popErrorToast, popSuccessToast } from '@app/shared/reducers/toast.reducer';
import { groupBy } from '@app/shared/utils/util';
import { Observable, map } from 'rxjs';

/**
 * Groups items/products by type: 'download' or 'other'.
 * @param items Array of items/products
 * @param getIntegration Function to extract integration array from item (should return string[] or undefined)
 */
export function groupItemsByType<T>(
	items: T[],
	getIntegration: (item: T) => string[] | undefined,
): Map<string, T[]> {
	return groupBy(items, (item) => (isDownloadableProduct(getIntegration(item)) ? 'download' : 'other'));
}

/**
 * Returns an observable that emits a Map of items grouped by type.
 * @param items$ Observable of items array
 * @param getIntegration Function to extract integration array from item (should return string[] or undefined)
 */
export function itemsByType$<T>(
	items$: Observable<T[]>,
	getIntegration: (item: T) => string[] | undefined,
): Observable<Map<string, T[]>> {
	return items$.pipe(map((items) => groupItemsByType(items, getIntegration)));
}

/**
 * Returns an observable that emits true if both 'download' and 'other' groups have items.
 * @param itemsByType$ Observable of Map<string, T[]>
 */
export function hasBothTypes$<T>(itemsByType$: Observable<Map<string, T[]>>): Observable<boolean> {
	return itemsByType$.pipe(
		map((groups) => {
			const values = [...groups.values()];
			return values.length > 1 && values.every((group) => group.length > 0);
		}),
	);
}

/**
 * Returns true if the given order product is downloadable.
 */
export function canBeDownloaded(product: CartItem | api.OrderProductDto): boolean {
	return (
		!!product?.product &&
		isDownloadableProduct(product?.product?.metadata?.order_integration ?? []) &&
		(!hasApprovalWorkflow(product.product) ||
			('approval_status' in product && product?.approval_status === 'Approved'))
	);
}

/**
 * Returns true if the order has any downloadable products
 */
export function hasDownloadableProducts(order: api.OrderDto): boolean {
	return order.products.some((product) => canBeDownloaded(product));
}

/**
 * Downloads the asset associated with an order product
 */
export function downloadProductAsset(
	product: Pick<api.OrderProductDto, 'printfile' | 'product'>,
	dispatch: (action: unknown) => void,
): void {
	const url = getProductAssetUrl(product);
	if (!url) {
		dispatch(
			popErrorToast({
				title: $localize`:@@OrderConfirmationDownloadError:Failed to download product`,
			}),
		);
		return;
	}
	window.open(url, '_blank');
}

/**
 * Downloads all available product assets for an order
 */
export function downloadAllProductAssets(
	order: api.OrderDto | null,
	dispatch: (action: unknown) => void,
	downloadAsset: (product: Pick<api.OrderProductDto, 'printfile' | 'product'>) => void,
): void {
	if (!order) return;

	const downloadableProducts = order.products.filter((product) => canBeDownloaded(product));

	if (downloadableProducts.length === 0) {
		dispatch(
			popErrorToast({
				title: $localize`:@@OrderConfirmationNoDownloadableProducts:No downloadable products available`,
			}),
		);
		return;
	}

	for (const [index, product] of downloadableProducts.entries()) {
		setTimeout(() => {
			downloadAsset(product);
		}, index * 300);
	}

	dispatch(
		popSuccessToast({
			title: $localize`:@@OrderConfirmationDownloadStarted:Download started`,
		}),
	);
}

/** Convert OrderProductDto to CartItemDto */
export function orderProductToCartItem(item: api.OrderProductDto): api.CartItemWithProductDto {
	return {
		id: item.uuid,
		product: item.product as api.CartItemProductDto,
		options: {
			selectedOptions: item.options.selected_options,
			printfile: item.printfile,
			workTitle: item.work_title,
		},
		price: item.price,
		productFlowData: {
			steps: item.product_flow_data?.steps ?? [],
			productSource: item.product_flow_data?.product_source,
			productOptionsSavedAt: item.product_flow_data?.product_options_saved_at,
			scaledProofImages: item.product_flow_data?.scaled_proof_images ?? [],
			proofImageThumbnail: item.product_flow_data?.proof_image_thumbnail,
			selectedWorkflow: item.product_flow_data?.selected_workflow,
			isDesignValidated: item.product_flow_data?.is_design_validated ?? true,
			designService: item.product_flow_data?.design_service,
		},
		isProcessing: false,
	} satisfies api.CartItemWithProductDto;
}

/** Convert OrderProductDto to CartItemDto. */
@Pipe({ name: 'orderProductsToCartItems', standalone: true })
export class OrderProductsToCartItemsPipe implements PipeTransform {
	/** Convert OrderProductDto to CartItemDto. */
	transform(items?: api.OrderProductDto[]): api.CartItemWithProductDto[] {
		return items?.map((item) => orderProductToCartItem(item)) ?? [];
	}
}
